import { useIntl } from 'react-intl';

import type { WorkflowListQuery } from '../../contracts/routines.types';
import { getAgentsDataConfig } from '../config';
import { useIsAuthenticated } from '../injected-auth';
import {
  useAddWorkflowEventRule as useAddWorkflowEventRuleMutation,
  useDeleteWorkflowEventRule as useDeleteWorkflowEventRuleMutation,
  useDeleteWorkflow as useDeleteWorkflowMutation,
  useDeleteWorkflowSchedule as useDeleteWorkflowScheduleMutation,
  useInstallPrimaryForConnector as useInstallPrimaryForConnectorMutation,
  useRunAnyWorkflow as useRunAnyWorkflowMutation,
  useRunWorkflow as useRunWorkflowMutation,
  useSetWorkflowEventRules as useSetWorkflowEventRulesMutation,
  useSetWorkflowSchedule as useSetWorkflowScheduleMutation,
  useShareWorkflow as useShareWorkflowMutation,
  useToggleWorkflowEnabled as useToggleWorkflowEnabledMutation,
  useToggleWorkflowSchedulePaused as useToggleWorkflowSchedulePausedMutation,
  useUnshareWorkflow as useUnshareWorkflowMutation,
  useUpdateWorkflow as useUpdateWorkflowMutation,
  useWorkflowShortcut as useWorkflowShortcutMutation,
  type RoutineActionedPort,
  type RoutineRunPorts,
} from '../mutation/routineMutations';
import { isCreditAllowanceExhausted } from '../projects/credit-allowance-exhausted';
import {
  useAllWorkflowRuns as useAllWorkflowRunsCore,
  useChatWorkflows as useChatWorkflowsCore,
  useConnectorWorkflowTemplates as useConnectorWorkflowTemplatesCore,
  useConnectorWorkflows as useConnectorWorkflowsCore,
  useFetchLatestRunWithProject as useFetchLatestRunWithProjectCore,
  useResolvableWorkflow as useResolvableWorkflowCore,
  useRoutineForProject as useRoutineForProjectCore,
  useSessionWorkflowRuns as useSessionWorkflowRunsCore,
  useSystemWorkflows as useSystemWorkflowsCore,
  useWorkflow as useWorkflowCore,
  useWorkflowEventRules as useWorkflowEventRulesCore,
  useWorkflowRuns as useWorkflowRunsCore,
  useWorkflowSchedule as useWorkflowScheduleCore,
  useWorkflowSchedules as useWorkflowSchedulesCore,
  useWorkflowTemplates as useWorkflowTemplatesCore,
  useWorkflows as useWorkflowsCore,
} from '../query/useQueryRoutines';
import { useExpectSessionRoutineRuns } from '../routines/useExpectSessionRoutineRuns';

/** Flag on and session ready — blocks Work queries before auth settles (pre-auth 401 → logout). */
const useAuthGatedEnabled = (flagEnabled: boolean): boolean => {
  const isAuthenticated = useIsAuthenticated();
  return flagEnabled && isAuthenticated;
};

const useWorkflowsFlag = (): boolean =>
  getAgentsDataConfig().useIsWorkflowsEnabled?.() ?? false;
const useSessionRoutineRunsFlag = (): boolean =>
  getAgentsDataConfig().useIsSessionRoutineRunsEnabled?.() ?? false;

/**
 * The preset catalog is server-localised, so the package keys its cache by
 * the active locale. Which locale is active is app policy, composed here
 * like the read gate.
 */
const useActiveLocale = (): string => useIntl().locale;

/**
 * Resolve the app reactions on each call so configuration stays live.
 */
const noop = (): void => {};
const noopAsync = async (): Promise<void> => {};
const analyticsPorts = (): RoutineActionedPort => ({
  onRoutineActioned: getAgentsDataConfig().routinesAnalytics?.onRoutineActioned ?? noop,
});
const refreshCreditAllowanceOnExhausted = (error: unknown) => {
  if (isCreditAllowanceExhausted(error)) {
    void getAgentsDataConfig().invalidateCreditAllowance?.();
  }
};

const runPorts = (): RoutineRunPorts => {
  const config = getAgentsDataConfig();
  return {
    onRunClicked: config.routinesAnalytics?.onRunClicked ?? noopAsync,
    onRunError: refreshCreditAllowanceOnExhausted,
    resolveRunRequestExtras: config.resolveWorkflowRunRequestExtras,
  };
};

// ── Queries ─────────────────────────────────────────────────────

export const useWorkflows = (
  query: WorkflowListQuery = {},
  { staleTime = 0, enabled = true }: { staleTime?: number; enabled?: boolean } = {}
) =>
  useWorkflowsCore({
    query,
    staleTime,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()) && enabled,
  });

/**
 * @deprecated Explore uses ``useWorkflowTemplates`` + ``workflowFromPreset``.
 * Kept for any leftover system-workflow callers.
 */
export const useSystemWorkflows = () =>
  useSystemWorkflowsCore({ enabled: useAuthGatedEnabled(useWorkflowsFlag()) });

export const useWorkflow = (workflowId: string | undefined) =>
  useWorkflowCore({
    workflowId,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

/** The Heidi-curated preset catalog shown in the Marketplace gallery. */
export const useWorkflowTemplates = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useWorkflowTemplatesCore({
    enabled: useAuthGatedEnabled(useWorkflowsFlag()) && enabled,
    locale: useActiveLocale(),
  });

/**
 * Presets bound to a Connected-apps provider (includes hidden integration ones).
 */
export const useConnectorWorkflowTemplates = (connector: string | undefined) =>
  useConnectorWorkflowTemplatesCore({
    connector,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
    locale: useActiveLocale(),
  });

/** Installed (including hidden) workflows for a Connected-apps provider. */
export const useConnectorWorkflows = (connector: string | undefined) =>
  useConnectorWorkflowsCore({
    connector,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

/**
 * Resolve a split-view selection id: persisted workflow from the API, or a
 * marketplace preset adapted into the Workflow card/conversation shape.
 */
export const useResolvableWorkflow = (workflowId: string | undefined) =>
  useResolvableWorkflowCore({
    workflowId,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
    locale: useActiveLocale(),
  });

export const useWorkflowSchedule = (workflowId: string | undefined) =>
  useWorkflowScheduleCore({
    workflowId,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

export const useWorkflowEventRules = (workflowId: string | undefined) =>
  useWorkflowEventRulesCore({
    workflowId,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

export const useWorkflowRuns = (
  workflowId: string | undefined,
  options?: { limit?: number }
) =>
  useWorkflowRunsCore({
    workflowId,
    limit: options?.limit,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

export const useAllWorkflowRuns = (
  workflowId?: string,
  options?: { enabled?: boolean }
) =>
  useAllWorkflowRunsCore({
    workflowId,
    // Both halves of the pre-carve `enabled` composed here: the app gate, and
    // the caller's opt-out that keeps the hook mounted without fetching.
    enabled: useAuthGatedEnabled(useWorkflowsFlag()) && options?.enabled !== false,
  });

export const useSessionWorkflowRuns = (
  sessionId: string | undefined,
  options?: { pollWhileLive?: boolean }
) => {
  const pollWhileAwaitingRuns = useExpectSessionRoutineRuns(sessionId);
  return useSessionWorkflowRunsCore({
    sessionId,
    // A different flag from every other read hook, as before.
    enabled: useAuthGatedEnabled(useSessionRoutineRunsFlag()),
    pollWhileAwaitingRuns,
    pollWhileLive: options?.pollWhileLive,
  });
};

/**
 * Imperatively resolve a routine's most recent openable run (one with a chat
 * project behind it). See the package hook for the cache-sharing contract.
 *
 * The gate here is the routines flag ALONE, NOT the auth-gated form — an
 * imperative `fetchQuery` has no `enabled` option, so the pre-carve hook read
 * `useWorkflowsFlag()` directly and returned null without fetching.
 * Preserved verbatim; adding the auth read would change when it no-ops.
 */
export const useFetchLatestRunWithProject = () =>
  useFetchLatestRunWithProjectCore({ enabled: useWorkflowsFlag() });

/**
 * Resolve the routine behind a Work project, when the project came from a
 * workflow run. See the package hook for the mapping and its scope guard.
 */
export const useRoutineForProject = (projectId: string | undefined) =>
  useRoutineForProjectCore({
    projectId,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

/** Fetch schedules for multiple workflows in a single batch request. */
export const useWorkflowSchedules = (workflowIds: string[]) =>
  useWorkflowSchedulesCore({
    workflowIds,
    enabled: useAuthGatedEnabled(useWorkflowsFlag()),
  });

/** Convenience: get all instructed (non-skill) workflows for the slash picker. */
export const useChatWorkflows = () =>
  useChatWorkflowsCore({ enabled: useAuthGatedEnabled(useWorkflowsFlag()) });

// ── Mutations ───────────────────────────────────────────────────

export const useInstallPrimaryForConnector = () =>
  useInstallPrimaryForConnectorMutation(analyticsPorts());

export const useUpdateWorkflow = (workflowId: string) =>
  useUpdateWorkflowMutation({ workflowId, ...analyticsPorts() });

/**
 * Persist a workflow's keyboard shortcut on the backend. Passing `''` clears it
 * (the backend maps `''` -> unset; do not send `null`).
 */
export const useWorkflowShortcut = (workflowId: string) =>
  useWorkflowShortcutMutation({ workflowId, ...analyticsPorts() });

export const useDeleteWorkflow = () => useDeleteWorkflowMutation(analyticsPorts());

export const useToggleWorkflowEnabled = (workflowId: string) =>
  useToggleWorkflowEnabledMutation({ workflowId, ...analyticsPorts() });

export const useShareWorkflow = (workflowId: string) =>
  useShareWorkflowMutation({ workflowId, ...analyticsPorts() });

export const useUnshareWorkflow = (workflowId: string) =>
  useUnshareWorkflowMutation({ workflowId, ...analyticsPorts() });

export const useRunWorkflow = (workflowId: string) =>
  useRunWorkflowMutation({ workflowId, ...runPorts() });

/** Like `useRunWorkflow` but accepts the workflow ID at call time. */
export const useRunAnyWorkflow = () => useRunAnyWorkflowMutation(runPorts());

// ── Schedule mutations ──────────────────────────────────────────

export const useSetWorkflowSchedule = (workflowId: string) =>
  useSetWorkflowScheduleMutation({ workflowId, ...analyticsPorts() });

export const useDeleteWorkflowSchedule = (workflowId: string) =>
  useDeleteWorkflowScheduleMutation({ workflowId, ...analyticsPorts() });

/**
 * Pause or resume the Temporal schedule linked to a workflow.
 * Pass ``true`` to pause, ``false`` to resume.
 */
export const useToggleWorkflowSchedulePaused = (workflowId: string) =>
  useToggleWorkflowSchedulePausedMutation({ workflowId, ...analyticsPorts() });

// ── Event rule mutations ────────────────────────────────────────

export const useAddWorkflowEventRule = (workflowId: string) =>
  useAddWorkflowEventRuleMutation({ workflowId, ...analyticsPorts() });

export const useDeleteWorkflowEventRule = (workflowId: string) =>
  useDeleteWorkflowEventRuleMutation({ workflowId, ...analyticsPorts() });

/**
 * Replace all event rules (delete existing, add new). Non-atomic; if the add
 * phase fails, the error toast warns the user.
 */
export const useSetWorkflowEventRules = (workflowId: string) =>
  useSetWorkflowEventRulesMutation({ workflowId, ...analyticsPorts() });
